const DOTS_SHARE = 30;
const PARIN_SHARE = 5;
export const BUY_IN = 35;

const RABBIT_RATE = 5;
const SQUIRREL_RATE = 2;

export function computeRounds(rounds) {
  let carry = 0;
  return rounds.map((round) => {
    const n = round.players.length;
    const totalDots = round.players.reduce((s, p) => s + p.dots, 0);
    const dotPot = n * DOTS_SHARE;
    const valuePerDot = totalDots > 0 ? dotPot / totalDots : 0;
    const carryIn = carry;
    const parPot = n * PARIN_SHARE + carryIn;
    const parWinnerCount = round.players.filter((p) => p.parIn).length;
    const parShare = parWinnerCount > 0 ? parPot / parWinnerCount : 0;
    // carryPar: false = an unclaimed Par In pot is handed back to the
    // field ($5 each) instead of carrying to the next round.
    const parRefunded = parWinnerCount === 0 && round.carryPar === false;
    carry = parWinnerCount > 0 || parRefunded ? 0 : parPot;
    const carryOut = carry;
    const rabbitWinners = round.players.filter((p) => p.leg === "rabbit");
    const squirrelWinners = round.players.filter((p) => p.leg === "squirrel");
    if (rabbitWinners.length > 1) {
      console.warn(
        `Cypress Boys data error: round ${round.date} has ${rabbitWinners.length} Rabbit winners (${rabbitWinners
          .map((p) => p.name)
          .join(", ")}). Exactly one player can win the Rabbit — fix the entry in rounds.js.`
      );
    }
    if (squirrelWinners.length > 1) {
      console.warn(
        `Cypress Boys data error: round ${round.date} has ${squirrelWinners.length} Squirrel winners (${squirrelWinners
          .map((p) => p.name)
          .join(", ")}). Exactly one player can win the Squirrel — fix the entry in rounds.js.`
      );
    }
    const hasRabbit = rabbitWinners.length > 0;
    const hasSquirrel = squirrelWinners.length > 0;
    const players = round.players
      .map((p) => {
        const dotWinnings = p.dots * valuePerDot;
        const rabbitNet = p.leg === "rabbit" ? RABBIT_RATE * (n - 1) : hasRabbit ? -RABBIT_RATE : 0;
        const squirrelNet = p.leg === "squirrel" ? SQUIRREL_RATE * (n - 1) : hasSquirrel ? -SQUIRREL_RATE : 0;
        const rabbitSquirrel = rabbitNet + squirrelNet;
        // A refunded Par In pot (carryPar: false, no winner) is real
        // money handed back, so it stays in the payout-check math —
        // but it is not Par In winnings and never shows in that column.
        const parInCalc = p.parIn ? parShare : 0;
        const parRefund = parRefunded ? parPot / n : 0;
        const totalWonCalc = dotWinnings + parInCalc + parRefund + rabbitSquirrel;
        // parIn may be the actual dollar amount handed out (e.g. 42)
        // when the table split differs from the calculated share.
        // Refunds are not winnings and never appear in this column.
        const parInWinnings = typeof p.parIn === "number" ? p.parIn : parInCalc;
        // paid = GROSS amount actually handed to the player in the
        // clubhouse (before buy-in). When present, it replaces the
        // calculated figure and NET displays paid minus $35.
        const totalWon = typeof p.paid === "number" ? p.paid - BUY_IN : totalWonCalc;
        return { ...p, dotWinnings, parInWinnings, rabbitNet, squirrelNet, rabbitSquirrel, totalWon, totalWonCalc, net: totalWon - BUY_IN };
      })
      .sort((a, b) => b.totalWon - a.totalWon);
    return { ...round, playerCount: n, totalDots, dotPot, valuePerDot, parPot, parShare, carryIn, carryOut, parWinnerCount, rabbitWinner: rabbitWinners[0]?.name ?? null, squirrelWinner: squirrelWinners[0]?.name ?? null, players };
  });
}

export function computeYTD(computed) {
  const byPlayer = new Map();
  let totalPot = 0;
  let totalDots = 0;
  let totalDotPot = 0;
  computed.forEach((r) => {
    totalPot += r.playerCount * BUY_IN + r.carryIn - r.carryOut;
    totalDots += r.totalDots;
    totalDotPot += r.dotPot;
    r.players.forEach((p) => {
      const e = byPlayer.get(p.name) || {
        name: p.name, rounds: 0, dots: 0, dotWinnings: 0, parInWinnings: 0, rabbitNet: 0, squirrelNet: 0, rabbitSquirrel: 0, totalWon: 0,
      };
      e.rounds += 1;
      e.dots += p.dots;
      e.dotWinnings += p.dotWinnings;
      e.parInWinnings += p.parInWinnings;
      e.rabbitNet += p.rabbitNet;
      e.squirrelNet += p.squirrelNet;
      e.rabbitSquirrel += p.rabbitSquirrel;
      e.totalWon += p.totalWon;
      byPlayer.set(p.name, e);
    });
  });
  const players = [...byPlayer.values()]
    .map((p) => ({ ...p, net: p.totalWon - p.rounds * BUY_IN }))
    .sort((a, b) => b.totalWon - a.totalWon);
  return {
    players,
    totalPot,
    totalDots,
    valuePerDot: totalDots > 0 ? totalDotPot / totalDots : 0,
    playerCount: byPlayer.size,
  };
}

// money: whole dollars for display. moneyExact: two decimals, for rates
// like Value Per Dot. All calculations run on raw unrounded values.
export const money = (v) => `${v < 0 ? "-$" : "$"}${Math.round(Math.abs(v))}`;

export const moneyExact = (v) => `${v < 0 ? "-$" : "$"}${Math.abs(v).toFixed(2)}`;

export const formatDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export function computeSeasonBadges(computed) {
  const legs = new Map();
  const names = new Set();
  computed.forEach((r) =>
    r.players.forEach((p) => {
      names.add(p.name);
      if (p.leg) legs.set(p.name, (legs.get(p.name) || 0) + 1);
    })
  );
  const badges = new Map();
  const add = (name, key) => {
    if (!badges.has(name)) badges.set(name, new Set());
    badges.get(name).add(key);
  };
  const maxLegs = Math.max(0, ...legs.values());
  if (maxLegs > 0) legs.forEach((v, n) => v === maxLegs && add(n, "trapper"));
  if (computed.length >= 3) {
    const last3 = computed.slice(-3);
    names.forEach((name) => {
      const hot = last3.every((r) => {
        const p = r.players.find((x) => x.name === name);
        return p && p.net > 0;
      });
      if (hot) add(name, "hot");
    });
  }
  return badges;
}

export function playerHistory(computed, name) {
  let cumulative = 0;
  return computed
    .map((r) => {
      const p = r.players.find((x) => x.name === name);
      if (!p) return null;
      cumulative += p.totalWon;
      return { date: r.date, day: r.day, dots: p.dots, totalWon: p.totalWon, net: p.net, parIn: p.parIn, leg: p.leg, cumulative };
    })
    .filter(Boolean);
}

export function rosterFromRounds(rounds) {
  const seen = [];
  rounds.forEach((r) =>
    r.players.forEach((p) => {
      if (!seen.includes(p.name)) seen.push(p.name);
    })
  );
  return seen;
}

// Season records, recomputed from the round data. Ties are all listed.
export function computeRecords(computed) {
  const best = (entries, key) => {
    const max = Math.max(...entries.map((e) => e[key]));
    return { value: max, holders: entries.filter((e) => e[key] === max) };
  };
  const perRound = computed.flatMap((r) => r.players.map((p) => ({ name: p.name, date: r.date, dots: p.dots, totalWon: p.totalWon })));
  const lastPlayed = new Map();
  const roundsPlayed = new Map();
  const gooseEggs = new Map();
  const lastEgg = new Map();
  computed.forEach((r) =>
    r.players.forEach((p) => {
      roundsPlayed.set(p.name, (roundsPlayed.get(p.name) || 0) + 1);
      lastPlayed.set(p.name, r.date);
      if (p.dots === 0) {
        gooseEggs.set(p.name, (gooseEggs.get(p.name) || 0) + 1);
        lastEgg.set(p.name, r.date);
      }
    })
  );
  const played = [...roundsPlayed].map(([name, rounds]) => ({ name, rounds, date: lastPlayed.get(name) }));
  const eggs = [...gooseEggs].map(([name, eggs]) => ({ name, eggs, date: lastEgg.get(name) }));
  return {
    mostDots: perRound.length ? best(perRound, "dots") : null,
    biggestPayday: perRound.length ? best(perRound, "totalWon") : null,
    mostRounds: played.length ? best(played, "rounds") : null,
    mostGooseEggs: eggs.length ? best(eggs, "eggs") : null,
  };
}
